import { useEffect, useRef, useState } from 'react';

interface VoiceSphereProps {
    size?: number;
    active?: boolean;
    intensity?: number;
    className?: string;
}

export default function VoiceSphere({ size = 280, active = false, intensity = 0.3, className = '' }: VoiceSphereProps) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const frameRef = useRef<number>(0);
    const [audioLevel, setAudioLevel] = useState(0);

    // Use intensity prop to determine if "active" visuals should show
    const isActive = active || intensity > 0.5;

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d')!;
        const dpr = window.devicePixelRatio || 1;
        canvas.width = size * dpr;
        canvas.height = size * dpr;
        ctx.scale(dpr, dpr);

        let time = 0;

        const draw = () => {
            time += 0.015;
            ctx.clearRect(0, 0, size, size);
            const cx = size / 2;
            const cy = size / 2;
            const baseRadius = size * 0.35;

            // Outer glow
            const glowGrad = ctx.createRadialGradient(cx, cy, baseRadius * 0.8, cx, cy, baseRadius * 1.5);
            glowGrad.addColorStop(0, isActive ? 'rgba(139, 92, 246, 0.2)' : 'rgba(139, 92, 246, 0.1)');
            glowGrad.addColorStop(1, 'transparent');
            ctx.fillStyle = glowGrad;
            ctx.fillRect(0, 0, size, size);

            // Orbital rings
            const ringCount = 3;
            for (let i = 0; i < ringCount; i++) {
                const ringRadius = baseRadius * (0.6 + i * 0.25);
                const rotation = time * (0.5 + i * 0.3) * (i % 2 === 0 ? 1 : -1);
                const wobble = isActive ? audioLevel * 5 : 0;

                ctx.save();
                ctx.translate(cx, cy);
                ctx.rotate(rotation);
                ctx.beginPath();
                ctx.ellipse(0, 0, ringRadius + wobble, ringRadius * 0.4, 0, 0, Math.PI * 2);
                ctx.strokeStyle = `rgba(139, 92, 246, ${0.12 + i * 0.06})`;
                ctx.lineWidth = 1.5;
                ctx.stroke();
                ctx.restore();
            }

            // Core gradient sphere
            const sphereGrad = ctx.createRadialGradient(
                cx - baseRadius * 0.2, cy - baseRadius * 0.2, 0,
                cx, cy, baseRadius
            );
            const pulse = isActive ? 1 + audioLevel * 0.15 : 1 + Math.sin(time * 2) * 0.03;

            sphereGrad.addColorStop(0, 'rgba(139, 92, 246, 0.6)');
            sphereGrad.addColorStop(0.4, 'rgba(100, 60, 200, 0.35)');
            sphereGrad.addColorStop(0.7, 'rgba(45, 212, 191, 0.15)');
            sphereGrad.addColorStop(1, 'transparent');

            ctx.beginPath();
            ctx.arc(cx, cy, baseRadius * pulse, 0, Math.PI * 2);
            ctx.fillStyle = sphereGrad;
            ctx.fill();

            // Inner luminance
            const innerGrad = ctx.createRadialGradient(
                cx - baseRadius * 0.3, cy - baseRadius * 0.3, 0,
                cx, cy, baseRadius * 0.6
            );
            innerGrad.addColorStop(0, 'rgba(200, 180, 255, 0.3)');
            innerGrad.addColorStop(1, 'transparent');
            ctx.fillStyle = innerGrad;
            ctx.fill();

            // Orbiting particles
            const particleCount = isActive ? 12 : 6;
            for (let i = 0; i < particleCount; i++) {
                const angle = time * (0.8 + i * 0.2) + (i * Math.PI * 2) / particleCount;
                const orbitRadius = baseRadius * (0.5 + (i % 3) * 0.3) + (isActive ? audioLevel * 8 : 0);
                const px = cx + Math.cos(angle) * orbitRadius;
                const py = cy + Math.sin(angle) * orbitRadius * 0.5;
                const pSize = 1.5 + (i % 3);
                const alpha = 0.3 + Math.sin(time + i) * 0.2;

                ctx.beginPath();
                ctx.arc(px, py, pSize, 0, Math.PI * 2);
                ctx.fillStyle = i % 2 === 0
                    ? `rgba(139, 92, 246, ${alpha})`
                    : `rgba(45, 212, 191, ${alpha})`;
                ctx.fill();
            }

            frameRef.current = requestAnimationFrame(draw);
        };

        draw();
        return () => cancelAnimationFrame(frameRef.current);
    }, [size, isActive, audioLevel]);

    // Simulate gentle audio levels when active
    useEffect(() => {
        if (!isActive) {
            setAudioLevel(0);
            return;
        }
        const interval = setInterval(() => {
            setAudioLevel(Math.random() * 0.6 + 0.2);
        }, 120);
        return () => clearInterval(interval);
    }, [isActive]);

    return (
        <div className={`relative ${className}`} style={{ width: size, height: size }}>
            <canvas
                ref={canvasRef}
                style={{ width: size, height: size, position: 'relative', zIndex: 10 }}
            />
            {/* Background pulse */}
            {isActive && (
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(139,92,246,0.15) 0%, transparent 70%)',
                        animation: 'sphere-breathe 2s ease-in-out infinite',
                    }}
                />
            )}
        </div>
    );
}
